import { useEffect, useRef } from 'react'

import { isEmpty } from '@/shared/utils'

interface UseUpdateFormDefaultValueProps {
  defaultValue: any
  value: any
  onUpdate: (value: any) => void
}

/**
 * This hook is used to update the form state when the defaultValue changes.
 * Only updates if the current value is empty or during initial setup.
 * Includes debouncing to prevent multiple rapid updates.
 * Can also handle initial value setup if shouldInitialize is true.
 */
export const useUpdateFormDefaultValue = ({ defaultValue, value, onUpdate }: UseUpdateFormDefaultValueProps) => {
  const prevDefaultValue = useRef(defaultValue)
  const prevValue = useRef(value)

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null

    if (timeout) {
      clearTimeout(timeout)
    }

    // Case 1: Initial state with defaultValue changed
    if (
      !isEmpty(defaultValue) &&
      prevDefaultValue.current !== defaultValue &&
      ((isEmpty(prevValue.current) && isEmpty(value)) ||
        (!isEmpty(prevValue.current) &&
          (prevValue.current === value || prevValue.current === prevDefaultValue.current)))
    ) {
      onUpdate(defaultValue)
      prevDefaultValue.current = defaultValue
      prevValue.current = defaultValue
    } else if (prevValue.current !== defaultValue) {
      prevValue.current = defaultValue
    }

    // Case 2: defaultValue was removed in settings, and the value is not changed
    if (
      isEmpty(defaultValue) &&
      prevDefaultValue.current !== defaultValue &&
      !isEmpty(prevValue.current) &&
      (prevValue.current === value || prevValue.current === prevDefaultValue.current)
    ) {
      onUpdate(null)
      prevDefaultValue.current = null
      prevValue.current = null
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, value])
}
